import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RouterConstant } from '../../routers/index';
import { useGamificationStore } from '../../utils/gamificationStore';
import { useLessonPlayerStore } from '../../utils/lessonPlayerStore';
import { LessonService } from '../../services/lessons/lesson.service';
import { gamificationService } from '../../services/gamification/gamification.service';

export const useLessonSummary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { ROUTERS_PATH } = RouterConstant;
  const { setAwardAndGetSummary } = useGamificationStore();
  const currentProgress = useLessonPlayerStore((s) => s.currentProgress);

  const state = location.state || {};
  const fullTopic = (
    state.fullTopic ||
    state.topic ||
    state.topicName ||
    state.topic_name ||
    state.subjectName ||
    state.subject ||
    state.lessonData?.topic ||
    state.lessonData?.title ||
    state.lessonData?.fullTopic ||
    state.lessonData?.name ||
    currentProgress?.fullTopic ||
    currentProgress?.topicName ||
    ''
  ).trim();

  const levelName = (
    state.levelName ||
    state.level ||
    state.level_name ||
    state.lessonData?.levelName ||
    state.lessonData?.level ||
    currentProgress?.levelName ||
    ''
  ).trim();

  const { lessonId, isPhaseSummary, isFinalSummary, optimisticStats } = state;
  
  const [loading, setLoading] = useState(!optimisticStats);
  const [pointsLoading, setPointsLoading] = useState(!optimisticStats);
  const [percentileLoading, setPercentileLoading] = useState(true);
  const [stats, setStats] = useState({
    pointsEarned: optimisticStats?.pointsEarned || 0,
    accuracy: optimisticStats?.accuracy || 0,
    completedCount: optimisticStats?.completedCount || 0,
    totalCount: optimisticStats?.totalCount || 0
  });
  const [percentile, setPercentile] = useState<number>(0);
  const [exerciseList, setExerciseList] = useState<number[]>(location.state?.exerciseList || []);

  const { learningTimeMs = 0 } = location.state || {};

  const formatDuration = (ms: number): string => {
    const totalSec = Math.floor(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      setPercentileLoading(true);
      if (!optimisticStats) setLoading(true);
      try {
        // 1. Fetch consolidated summary data (Appends award and fetches percentile + lesson points in a single round-trip!)
        const summaryData = await setAwardAndGetSummary(isPhaseSummary ? 'lesson_phase_complete' : 'lesson_complete', { 
          lesson_id: lessonId,
          topic: fullTopic,
          phase_index: location.state?.phaseIndex
        });
 
        if (summaryData) {
          setPercentile(summaryData.percentile);
          setStats(prev => ({
            ...prev,
            pointsEarned: (summaryData.points && summaryData.points > 0)
              ? summaryData.points
              : (prev.pointsEarned || summaryData.gamification_state?.delta || 0),
          }));
        }

        // 2. Robust fallback for exerciseList if not passed or empty in state
        let effectiveList = location.state?.exerciseList || [];
        if (effectiveList.length === 0) {
          const lessonService = new LessonService();
          const lesson = location.state?.lessonData || (lessonId ? await lessonService.getLesson(lessonId) : null);
          if (lesson?.phases) {
            // Find current phase based on phaseIndex passed in state
            const currentPhase = (location.state?.phaseIndex ? lesson.phases[location.state.phaseIndex - 1] : null);
            
            if (currentPhase?.exercise_list) {
              try {
                const list = Array.isArray(currentPhase.exercise_list) 
                  ? currentPhase.exercise_list 
                  : JSON.parse(currentPhase.exercise_list);
                if (Array.isArray(list) && list.length > 0) {
                  effectiveList = list;
                }
              } catch (e) {
                console.error("Error parsing backup exercise_list", e);
              }
            }
          }
        }
        setExerciseList(effectiveList);
        
        setPointsLoading(false);
        setPercentileLoading(false);
      } catch (error) {
        console.error("Failed to fetch summary data", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [setAwardAndGetSummary, lessonId, fullTopic, isPhaseSummary, location.state?.phaseIndex, location.state?.exerciseList]);

  const handleNavigate = (path: string, bypassExercisePractice = false) => {
    const currentList = (location.state?.exerciseList && location.state.exerciseList.length > 0) ? location.state.exerciseList : exerciseList;

    if (!bypassExercisePractice && (path === ROUTERS_PATH.PLAY_LESSON || (path === ROUTERS_PATH.SEARCH_LESSON && isFinalSummary))) {
      if (currentList.length > 0) {
        navigate(ROUTERS_PATH.EXERCISE_PRACTICE, {
          state: {
            exercise_list: currentList,
            topic_name: fullTopic,
            lessonId,
            levelName,
            lessonData: location.state?.lessonData,
            phaseIndex: location.state?.phaseIndex,
            isFinalSummary: isFinalSummary
          }
        });
        return;
      }
      
      if (isPhaseSummary) {
        // Resume from where we left off
        navigate(ROUTERS_PATH.PLAY_LESSON, { 
          state: { 
            lessonId, 
            resume: true,
            levelName,
            topicName: fullTopic,
            lessonData: location.state?.lessonData
          } 
        });
      } else {
        // Restart or search lesson
        navigate(path, { state: { lessonId } });
      }
      return;
    }
    navigate(path);
  };

  return {
    t,
    loading,
    pointsLoading,
    percentileLoading,
    stats,
    fullTopic,
    levelName,
    isPhaseSummary,
    isFinalSummary,
    percentile,
    learningTime: formatDuration(learningTimeMs),
    handleNavigate,
    ROUTERS_PATH,
    exerciseList
  };
};
